import { isBefore, parseISO, startOfDay } from "date-fns";
import { capitalize } from "@common/helpers/string";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";

interface OccurrenceDisplayStatus {
    label: string;
    /** Explains why the label differs from the occurrence's raw status; null when no override applies. */
    tooltip: string | null;
}

/** Raw statuses a template-archived/recurrence-paused override can mask (per PRO-10574: "would show scheduled or skipped"). */
const MASKABLE_STATUSES: ServiceTemplateRecurrenceOccurrenceStatus[] = [
    ServiceTemplateRecurrenceOccurrenceStatus.SCHEDULED,
    ServiceTemplateRecurrenceOccurrenceStatus.SKIPPED,
];

/**
 * Resolves what an occurrence's status cell should actually display. The raw
 * `status` column alone can be misleading, so this layers three overrides on
 * top of it (see PRO-10574):
 *
 * 1. Priority 1 — a SCHEDULED occurrence whose `applicationDate` has already
 *    passed never got (and, since the daily auto-apply job only ever applies
 *    occurrences due on or before today, never will get) auto-applied for
 *    that date. Display it as "Skipped" regardless of any archived/paused
 *    state below.
 * 2. If the occurrence's template has been archived while it was still
 *    SCHEDULED or SKIPPED, show "Archived" instead, with a tooltip.
 * 3. If the recurrence (schedule) has been paused while it was still
 *    SCHEDULED or SKIPPED, show "Paused" instead, with a tooltip.
 *
 * Otherwise, the raw status is shown as-is.
 */
export function getOccurrenceDisplayStatus({
    status,
    applicationDate,
    isTemplateArchived,
    isRecurrencePaused,
}: {
    status: ServiceTemplateRecurrenceOccurrenceStatus | undefined;
    applicationDate: string;
    isTemplateArchived: boolean;
    isRecurrencePaused: boolean;
}): OccurrenceDisplayStatus {
    if (!status) {
        return { label: "", tooltip: null };
    }

    const isOverdueAndUnapplied =
        status === ServiceTemplateRecurrenceOccurrenceStatus.SCHEDULED &&
        isBefore(parseISO(applicationDate), startOfDay(new Date()));

    if (isOverdueAndUnapplied) {
        return { label: "Skipped", tooltip: null };
    }

    if (MASKABLE_STATUSES.includes(status)) {
        if (isTemplateArchived) {
            return {
                label: "Archived",
                tooltip:
                    "This occurrence's template has been archived, so it won't be automatically applied.",
            };
        }
        if (isRecurrencePaused) {
            return {
                label: "Paused",
                tooltip:
                    "This schedule has been paused, so this occurrence won't be automatically applied.",
            };
        }
    }

    return { label: capitalize(status), tooltip: null };
}
